import * as React from "react";
import { ApolloError, gql, useMutation } from "@apollo/client";

import {
  DeleteAlertResponse,
  MutationDeleteAlertArgs,
} from "../../../../schemaTypes";
import { NormalizedErrorMap } from "../../../../types/NormalizedErrorMap";
import { normalizeErrors } from "../../../../utils/normalizeErrors";

type DeleteAlertMutationResult = {
  deleteAlert: DeleteAlertResponse;
};

export const deleteAlertMutation = gql`
  mutation DeleteAlertMutation($id: String!) {
    deleteAlert(id: $id) {
      errors {
        path
        message
      }
      message
    }
  }
`;

interface Props {
  onMessage?: (message: string) => void;
  children: (
    data: {
      submit: (
        values: MutationDeleteAlertArgs
      ) => Promise<{
        errors: NormalizedErrorMap | null;
        message?: string;
      }>;
      loading: boolean;
      error?: ApolloError;
    }
  ) => React.ReactElement | null;
}

export const DeleteAlertController: React.FC<Props> = ({ children, onMessage }) => {
  const [deleteAlert, { loading, error }] = useMutation<
    DeleteAlertMutationResult,
    MutationDeleteAlertArgs
  >(deleteAlertMutation);

  const submit = async (values: MutationDeleteAlertArgs) => {
    const { data } = await deleteAlert({
      variables: {
        id: values.id,
      },
    });

    const response = data?.deleteAlert;
    const errors = response?.errors ? normalizeErrors(response.errors) : null;
    const message = response?.message ?? undefined;

    if (message && onMessage) {
      onMessage(message);
    }

    return {
      errors,
      message,
    };
  };

  return children({ submit, loading, error });
};
